import styled from 'styled-components';
import {
  color,
  ColorProps,
  display,
  DisplayProps,
  maxWidth,
  MaxWidthProps,
  minWidth,
  MinWidthProps,
  space,
  SpaceProps,
  width,
  WidthProps
} from 'styled-system';

/**
 * Input Wrapper
 */
export type StyledInputProps = WidthProps &
  MinWidthProps &
  MaxWidthProps &
  ColorProps &
  SpaceProps &
  DisplayProps & {
    error?: boolean;
    multiline?: boolean;
  };

export const StyledInputBase = styled.input``;

export const StyledInput = styled.div<StyledInputProps>`
  position: relative;
  padding-top: 15px;
  margin-bottom: 15px;
  width: 100%;
  height: ${({ multiline }) => (multiline ? 'auto' : '50px')};

  & button {
    position: absolute;
    font-size: 0.875rem;
    color: #3564fd;
    right: 0;
    top: 21px;
    border: none;
    background: transparent;
  }

  & label {
    position: absolute;
    top: 0;
    display: block;
    transition: 0.2s;
    font-size: 0.75rem;
    line-height: 1.33;
    color: ${({ theme, error }) =>
      error ? theme.colors.red[70] : theme.colors.grey[80]};
  }

  & span {
    align-self: flex-start;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: normal;
    color: ${({ theme, error }) =>
      error ? theme.colors.red[70] : theme.colors.grey[90]};
    margin-top: 3px;
    display: block;
  }

  & ${StyledInputBase} {
    font-family: inherit;
    width: 100%;
    border: 0;
    border-bottom-width: 1px;
    border-bottom-style: solid;
    border-radius: 0;
    border-color: ${({ theme, error }) =>
      error ? theme.colors.red[40] : theme.colors.grey[50]};
    outline: 0;
    font-size: 1rem;
    color: ${({ theme }) => theme.colors.grey[90]};
    line-height: 1.3rem;
    padding: 5px 0;
    background: transparent;
    transition: border-color 0.2s;

    &::placeholder {
      color: transparent;
    }

    &:-webkit-autofill ~ label {
      transition: none;
    }

    &:placeholder-shown ~ label,
    &:placeholder-shown:-webkit-autofill ~ label {
      font-size: 1rem;
      cursor: text;
      top: 20px;
      color: ${({ theme }) => theme.colors.grey[80]};
    }
    &:required,
    &:invalid {
      box-shadow: none;
    }
    &:focus {
      padding-bottom: 5px;
      border-color: ${({ theme, error }) =>
        error ? theme.colors.red[40] : theme.colors.blue[50]};

      & ~ label {
        position: absolute;
        top: 0;
        display: block;
        transition: 0.2s;
        font-size: 0.75rem;
        line-height: 1.33;
        color: ${({ theme, error }) =>
          error ? theme.colors.red[70] : theme.colors.blue[50]};
      }
    }

    &:disabled {
      color: ${({ theme }) => theme.colors.grey[50]};
      & ~ label,
      & ~ span {
        color: ${({ theme }) => theme.colors.grey[50]};
      }

      &:-webkit-autofill:first-line {
        color: ${({ theme }) => theme.colors.grey[50]};
      }
    }

    /** CSS Workaround to hide autofill background */
    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-webkit-autofill:active {
      transition: color 9999s ease-out, background-color 9999s ease-out;
      transition-delay: 9999s;

      &:first-line {
        font-size: 1rem;
        font-family: 'Public Sans', sans-serif;
      }
    }
  }

  ${space}
  ${color}
  ${display}
  ${width}
  ${maxWidth}
  ${minWidth}
`;

export const StyledOutlinedInput = styled(StyledInput)`
  display: flex;
  flex-direction: column;
  margin-top: 0;
  padding-top: 0;

  & ${StyledInputBase} {
    background: #fff;
    color: ${({ theme }) => theme.colors.grey[80]};
    order: 2;
    border-width: 1px;
    border-style: solid;
    border-color: ${({ theme, error }) =>
      error ? theme.colors.red[70] : theme.colors.grey[30]};
    border-radius: 8px;
    box-sizing: border-box;
    padding: 12px 18px;
    margin-bottom: 8px;

    &:disabled::placeholder {
      color: ${({ theme }) => theme.colors.grey[50]};
    }

    &::placeholder {
      color: ${({ theme }) => theme.colors.grey[80]};
    }

    &:focus {
      border-color: ${({ theme }) => theme.active};
      padding-bottom: 12px;

      &::placeholder {
        color: transparent;
      }

      & ~ label {
        display: none;
      }
    }
  }

  & textarea${StyledInputBase} {
    padding: 24px;
    &:focus {
      padding-bottom: 24px;
    }
  }

  & label {
    display: none;
  }

  & span {
    order: 3;
  }
`;
